import React, { useMemo } from 'react';
import ProductCard from './ProductCard';
import { SkeletonCard } from './Skeleton';
import { Link } from 'react-router-dom';

const ProductShowcase = ({ title, products, subtitle, loading, badge, showSeeMore = true, showExclusiveBadge = true, seeMoreLink, rowCount = 2, mobileRows = 3 }) => {
  const singleRow = rowCount === 1;
  const row1Products = useMemo(
    () => (singleRow ? (products || []) : (products || []).filter((_, i) => i % 2 === 0)),
    [products, singleRow]
  );
  const row2Products = useMemo(
    () => (singleRow ? [] : (products || []).filter((_, i) => i % 2 === 1)),
    [products, singleRow]
  );

  if (!loading && (!products || products.length === 0)) {
    return null;
  }

  return (
    <section className="py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold">{title}</h2>
            {subtitle && (
              <p className="text-gray-600 mt-2 text-sm">{subtitle}</p>
            )}
            <div className="w-16 h-0.5 bg-gold mx-auto mt-2"></div>
          </div>
          {!loading && showSeeMore && (
            <Link
              to={seeMoreLink || '/shop-all'}
              className="group inline-flex items-center gap-2 px-6 py-2 bg-gold text-white border border-gold font-medium rounded-lg hover:bg-gold/80 transition-all duration-300 ease-in-out text-sm"
            >
              See More
              <span className="transform translate-x-0 group-hover:translate-x-1 transition-transform duration-300">
                →
              </span>
            </Link>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {[...Array(mobileRows * 2)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 aspect-[3/4] rounded-xl mb-4"></div>
                <div className="bg-gray-200 h-4 rounded w-3/4 mb-2"></div>
                <div className="bg-gray-200 h-4 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* MOBILE STATIC GRID — visible only on mobile */}
            <div className="sm:hidden">
              {loading ? (
                <div className="grid grid-cols-2 gap-4">
                  {[...Array(mobileRows * 2)].map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {products.slice(0, mobileRows * 2).map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}
            </div>

            {/* DESKTOP STATIC GRID — hidden on mobile */}
            <div className="hidden sm:block">
              <div
                className="overflow-hidden w-full"
                style={{
                  position: 'relative',
                  maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
                }}
              >
                <div className="flex flex-col gap-4 md:gap-6">
                  {/* ROW 1 */}
                  {row1Products.length > 0 && (
                    <div className="overflow-hidden w-full">
                      <div
                        className="flex gap-4 md:gap-6 items-stretch"
                        style={{ width: 'max-content' }}
                      >
                        {row1Products.map((product, index) => (
                          <div
                            key={`row1-${product.id || index}`}
                            className="flex-shrink-0"
                            style={{ width: '180px' }}
                          >
                            <ProductCard
                              product={product}
                              badge={badge}
                              isNew={product.is_new}
                              isExclusive={showExclusiveBadge ? undefined : false}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ROW 2 */}
                  {!singleRow && row2Products.length > 0 && (
                    <div className="overflow-hidden w-full">
                      <div
                        className="flex gap-4 md:gap-6 items-stretch"
                        style={{ width: 'max-content' }}
                      >
                        {row2Products.map((product, index) => (
                          <div
                            key={`row2-${product.id || index}`}
                            className="flex-shrink-0"
                            style={{ width: '180px' }}
                          >
                            <ProductCard
                              product={product}
                              badge={badge}
                              isNew={product.is_new}
                              isExclusive={showExclusiveBadge ? undefined : false}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default ProductShowcase;
